import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Undo2 } from 'lucide-react';

const isDev = process.env.NODE_ENV === 'development';

/**
 * MatchingQuestion Component
 * 
 * Renders pairs that user must match with full edit/undo capability before submission.
 * State management:
 * - userMatches: { leftIndex: rightIndex } - persisted in parent
 * - selectedLeft: which left term is currently selected (local state)
 * 
 * Edit/Undo Behavior (before Submit):
 * - Click a matched term → UNMATCH it (removes the pair, frees the definition)
 * - Click a matched definition → UNMATCH it (removes the pair, frees the term)
 * - Select a new definition while a term is selected → OVERWRITE previous match
 * - Submit disabled until all required pairs are matched
 * - After submission, matches lock (no edits allowed)
 * 
 * Flow:
 * 1. Click left term → highlights it (or unmatches if already matched)
 * 2. Click right definition → creates/overwrites match, clears selectedLeft
 * 3. Repeat until all pairs matched
 * 4. Submit becomes enabled when Object.keys(userMatches).length === pairs.length
 */
export default function MatchingQuestion({
  question,
  userMatches = {},  // { leftIndex: rightIndex } - PARENT CONTROLLED
  onMatchChange,     // Callback to update parent state
  showResult = false,
  disabled = false
}) {
  // Local state for currently selected left term
  const [selectedLeft, setSelectedLeft] = useState(null);
  
  // Extract pairs from question data - memoized for stability
  const pairs = useMemo(() => {
    if (!question) return [];
    
    // Shape 1: pairs array [{ left, right }]
    if (Array.isArray(question.pairs) && question.pairs.length > 0) {
      return question.pairs.map((p, i) => ({
        leftIndex: i,
        left: p.left || p.term || p.question || '',
        rightIndex: i,
        right: p.right || p.definition || p.answer || ''
      }));
    }
    
    // Shape 2: matching_pairs array
    if (Array.isArray(question.matching_pairs) && question.matching_pairs.length > 0) {
      return question.matching_pairs.map((p, i) => ({
        leftIndex: i,
        left: p.left || p.term || p.question || '',
        rightIndex: i,
        right: p.right || p.definition || p.answer || ''
      }));
    }
    
    // Shape 3: left_column and right_column arrays
    if (Array.isArray(question.left_column) && Array.isArray(question.right_column)) {
      return question.left_column.map((left, i) => ({
        leftIndex: i,
        left: left,
        rightIndex: i,
        right: question.right_column[i] || ''
      }));
    }
    
    console.warn('[TEI_RENDERER] matching: No valid pairs data found in question');
    return [];
  }, [question]);
  
  // Shuffle right column indices - stable across re-renders using question as seed
  const shuffledRightIndices = useMemo(() => {
    if (pairs.length === 0) return [];
    
    // Create a seeded shuffle based on question text for consistency
    const seed = question?.question_text?.length || 42;
    const indices = pairs.map((_, i) => i);
    
    // Fisher-Yates shuffle with seeded random
    let seedValue = seed;
    const seededRandom = () => {
      seedValue = (seedValue * 9301 + 49297) % 233280;
      return seedValue / 233280;
    };
    
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    
    return indices;
  }, [pairs, question?.question_text]);
  
  // Log state for debugging
  useEffect(() => {
    if (isDev) console.log(`[TEI_RENDERER] matching state: pairs=${pairs.length}, matches=${JSON.stringify(userMatches)}, selectedLeft=${selectedLeft}`);
  }, [pairs.length, userMatches, selectedLeft]);
  
  // Handle clicking a left term
  const handleLeftClick = useCallback((leftIndex) => {
    if (disabled || showResult) return;
    
    // If this term is already matched, UNMATCH it (edit/undo capability)
    if (userMatches[leftIndex] !== undefined) {
      if (isDev) console.log(`[TEI_RENDERER] matching: UNMATCH left=${leftIndex} (was matched to right=${userMatches[leftIndex]})`);
      const newMatches = { ...userMatches };
      delete newMatches[leftIndex];
      onMatchChange(newMatches);
      setSelectedLeft(null); // Clear any selection
      return;
    }
    
    // Toggle selection - if already selected, deselect
    if (selectedLeft === leftIndex) {
      if (isDev) console.log(`[TEI_RENDERER] matching: deselecting left=${leftIndex}`);
      setSelectedLeft(null);
      return;
    }
    
    if (isDev) console.log(`[TEI_RENDERER] matching: selecting left=${leftIndex}`);
    setSelectedLeft(leftIndex);
  }, [disabled, showResult, userMatches, selectedLeft, onMatchChange]);
  
  // Handle clicking a right definition
  const handleRightClick = useCallback((rightIndex) => {
    if (disabled || showResult) return;
    
    // Find if this definition is already matched to some term
    const existingMatchEntry = Object.entries(userMatches).find(([_, r]) => r === rightIndex);
    
    // If no left term is selected:
    if (selectedLeft === null) {
      // If this definition is matched, UNMATCH it (edit/undo capability)
      if (existingMatchEntry) {
        const [matchedLeftIndex, _] = existingMatchEntry;
        if (isDev) console.log(`[TEI_RENDERER] matching: UNMATCH right=${rightIndex} (was matched from left=${matchedLeftIndex})`);
        const newMatches = { ...userMatches };
        delete newMatches[matchedLeftIndex];
        onMatchChange(newMatches);
      } else {
        if (isDev) console.log(`[TEI_RENDERER] matching: no left selected, ignoring right click`);
      }
      return;
    }
    
    // A left term IS selected - create or overwrite the match
    // If this definition was matched to a DIFFERENT term, free that term first
    let newMatches = { ...userMatches };
    if (existingMatchEntry) {
      const [oldLeftIndex, _] = existingMatchEntry;
      if (parseInt(oldLeftIndex) !== selectedLeft) {
        if (isDev) console.log(`[TEI_RENDERER] matching: OVERWRITE - right=${rightIndex} was matched to left=${oldLeftIndex}, now remapping to left=${selectedLeft}`);
        delete newMatches[oldLeftIndex];
      }
    }
    
    // Create the match
    if (isDev) console.log(`[TEI_RENDERER] matching: creating match left=${selectedLeft} -> right=${rightIndex}`);
    newMatches[selectedLeft] = rightIndex;
    if (isDev) console.log(`[TEI_RENDERER] matching: new matches state:`, newMatches);
    
    // Update parent state
    onMatchChange(newMatches);
    
    // Clear selection
    setSelectedLeft(null);
  }, [disabled, showResult, selectedLeft, userMatches, onMatchChange]);
  
  // Check if a left item is matched
  const isLeftMatched = useCallback((leftIndex) => {
    return userMatches[leftIndex] !== undefined;
  }, [userMatches]);
  
  // Get what right index a left item is matched to
  const getMatchedRight = useCallback((leftIndex) => {
    return userMatches[leftIndex];
  }, [userMatches]);
  
  // Check if a right item is matched
  const isRightMatched = useCallback((rightIndex) => {
    return Object.values(userMatches).includes(rightIndex);
  }, [userMatches]);
  
  // Check if a match is correct (for result display)
  const isMatchCorrect = useCallback((leftIndex, rightIndex) => {
    return leftIndex === rightIndex;
  }, []);
  
  // Count matched pairs
  const matchedCount = Object.keys(userMatches).length;
  const totalPairs = pairs.length;
  
  // Empty state
  if (pairs.length === 0) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="matching-question">
        <p className="text-amber-700">No matching pairs found for this question.</p>
      </div>
    );
  }
  
  return (
    <div data-testid="matching-question">
      <p className="text-sm text-muted-foreground mb-4">
        Click a term on the left, then click its match on the right. 
        <span className="text-xs ml-1">(Click any matched item to unmatch it)</span>
      </p>
      
      <div className="grid grid-cols-2 gap-4">
        {/* LEFT COLUMN - Terms */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Terms</p>
          {pairs.map((pair, idx) => {
            const isSelected = selectedLeft === idx;
            const isMatched = isLeftMatched(idx);
            const matchedToRight = getMatchedRight(idx);
            const isCorrect = showResult && isMatched && isMatchCorrect(idx, matchedToRight);
            const isIncorrect = showResult && isMatched && !isMatchCorrect(idx, matchedToRight);
            
            let itemClass = "w-full p-3 rounded-lg border-2 text-left text-sm transition-all";
            
            if (showResult) {
              if (isCorrect) {
                itemClass += " border-green-500 bg-green-50";
              } else if (isIncorrect) {
                itemClass += " border-red-500 bg-red-50";
              } else if (!isMatched) {
                itemClass += " border-amber-500 bg-amber-50";
              }
            } else {
              if (isSelected) {
                itemClass += " border-sage-500 bg-sage-100 ring-2 ring-sage-300 cursor-pointer";
              } else if (isMatched) {
                // Matched but still clickable for undo - show subtle hover state
                itemClass += " border-sage-400 bg-sage-50 cursor-pointer hover:border-amber-400 hover:bg-amber-50";
              } else {
                itemClass += " border-border bg-background hover:border-sage-300 cursor-pointer";
              }
            }
            
            return (
              <button
                key={`left-${idx}`}
                onClick={() => handleLeftClick(idx)}
                disabled={disabled || showResult}
                className={itemClass}
                data-testid={`matching-left-${idx}`}
                title={isMatched && !showResult ? "Click to unmatch" : undefined}
              >
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center text-xs font-medium flex-shrink-0">
                    {idx + 1}
                  </span>
                  <span className="flex-grow">{pair.left}</span>
                  {isMatched && !showResult && (
                    <span className="flex items-center gap-1 flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-sage-500" />
                      <Undo2 className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-amber-500" />
                    </span>
                  )}
                  {showResult && isMatched && (
                    isCorrect 
                      ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      : <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
        
        {/* RIGHT COLUMN - Definitions (shuffled) */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Definitions</p>
          {shuffledRightIndices.map((originalIdx, displayIdx) => {
            const pair = pairs[originalIdx];
            if (!pair) return null;
            
            const isMatched = isRightMatched(originalIdx);
            const canSelect = selectedLeft !== null && !isMatched;
            
            // Find which left item this is matched to (if any)
            const matchedByLeft = Object.entries(userMatches).find(([_, r]) => r === originalIdx)?.[0];
            const isCorrectMatch = showResult && matchedByLeft !== undefined && isMatchCorrect(parseInt(matchedByLeft), originalIdx);
            const isIncorrectMatch = showResult && matchedByLeft !== undefined && !isMatchCorrect(parseInt(matchedByLeft), originalIdx);
            
            let itemClass = "w-full p-3 rounded-lg border-2 text-left text-sm transition-all";
            
            if (showResult) {
              if (isCorrectMatch) {
                itemClass += " border-green-500 bg-green-50";
              } else if (isIncorrectMatch) {
                itemClass += " border-red-500 bg-red-50";
              } else {
                itemClass += " border-border bg-background";
              }
            } else {
              if (isMatched) {
                // Matched but still clickable for undo
                itemClass += " border-sage-400 bg-sage-50 cursor-pointer hover:border-amber-400 hover:bg-amber-50";
              } else if (canSelect) {
                itemClass += " border-sage-300 bg-sage-50/50 hover:border-sage-500 hover:bg-sage-100 cursor-pointer";
              } else {
                // Unmatched, no selection active - still clickable if user wants to select
                itemClass += " border-border bg-background cursor-default";
              }
            }
            
            return (
              <button
                key={`right-${originalIdx}`}
                onClick={() => handleRightClick(originalIdx)}
                disabled={disabled || showResult}
                className={itemClass}
                data-testid={`matching-right-${originalIdx}`}
                title={isMatched && !showResult ? "Click to unmatch" : undefined}
              >
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center text-xs font-medium flex-shrink-0">
                    {String.fromCharCode(65 + displayIdx)}
                  </span>
                  <span className="flex-grow">{pair.right}</span>
                  {isMatched && !showResult && (
                    <span className="flex items-center gap-1 flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-sage-500" />
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Selection helper message */}
      {selectedLeft !== null && !showResult && (
        <p className="mt-3 text-sm text-sage-600 text-center font-medium">
          Now click a definition to match with "{pairs[selectedLeft]?.left}"
        </p>
      )}
      
      {/* Progress indicator */}
      {!showResult && (
        <p className="mt-3 text-xs text-muted-foreground text-center">
          {matchedCount} of {totalPairs} matched
        </p>
      )}
      
      {/* Detailed Explanation after submission - show correct pairings */}
      {showResult && (
        <div className="mt-6 p-4 bg-secondary/50 rounded-xl border border-border" data-testid="matching-explanation">
          <p className="text-sm font-medium text-sage-900 mb-3">Correct Matches:</p>
          <div className="space-y-2">
            {pairs.map((pair, idx) => {
              const userMatchedRight = userMatches[idx];
              const isCorrect = userMatchedRight === idx;
              
              return (
                <div 
                  key={`explanation-${idx}`} 
                  className={`flex items-start gap-2 p-2 rounded-lg text-sm ${
                    isCorrect ? 'bg-green-50' : 'bg-red-50'
                  }`}
                >
                  {isCorrect ? (
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <span className="font-medium text-sage-900">{pair.left}</span>
                    <span className="text-muted-foreground mx-2">→</span>
                    <span className="text-sage-700">{pair.right}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {question?.explanation && (
            <p className="mt-3 text-xs text-muted-foreground italic">
              {question.explanation}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
